import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useWalletModal } from './WalletModalContext';
import WALLETS from './wallets';
import '../styles/ConnectWalletModal.css';
import ProfileModal from "./ProfileModal"; // adjust path to wherever ProfileModal.jsx actually lives

// The short list shown first. Each entry looks itself up in the full
// WALLETS list by these aliases and uses that real image if found -
// that's what makes Bifrost's icon show (matches id 'bifrost') and
// MetaMask's (matches id 'fox', since that's the filename you have for
// it). WalletConnect and Ledger have no matching file yet, so they fall
// back to a colored initial until you add e.g. walletconnect.png /
// ledger.png to public/wallet-logos and to src/data/wallets.js - once
// that entry exists, it'll show here automatically, no code change needed.
const QUICK_WALLET_DEFS = [
  { id: 'Phantom', name: 'Phantom', aliases: ['phantom'], color: '#5B4FE9' },
  { id: 'metamask', name: 'MetaMask', aliases: ['metamask'], color: '#F5841F' },
  {
    id: 'walletconnect',
    name: 'WalletConnect',
    aliases: ['wallet-io'],
    color: '#3B99FC',
  },
  {
    id: 'ledger',
    name: 'Ledger Wallet',
    aliases: ['ledger'],
    color: '#111214',
    outline: true,
  },
];

function resolveQuickWallet(def) {
  const match = WALLETS.find((w) => def.aliases.includes(w.id));
  return match ? { ...def, icon: match.icon } : def;
}

const QUICK_WALLETS = QUICK_WALLET_DEFS.map(resolveQuickWallet);

const PALETTE = ['#7C5CFF', '#3FE8D0', '#4D7DFF', '#FF6AD5', '#F5841F', '#5B4FE9'];

function colorForName(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

function WalletIcon({ wallet, size = 40 }) {
  const style = { width: size, height: size };
  if (wallet.isAll) {
    return (
      <span className="wallet-icon wallet-icon--all" style={style}>
        <span className="wallet-icon__grid">
          <i />
          <i />
          <i />
          <i />
        </span>
      </span>
    );
  }
  if (wallet.icon) {
    return (
      <span className="wallet-icon wallet-icon--image" style={style}>
        <img src={wallet.icon} alt="" loading="lazy" />
      </span>
    );
  }
  return (
    <span
      className={`wallet-icon ${wallet.outline ? 'wallet-icon--outline' : ''}`}
      style={{ ...style, background: wallet.color || colorForName(wallet.name || '?') }}
    >
      {(wallet.name || '?').charAt(0).toUpperCase()}
    </span>
  );
}

export default function ConnectWalletModal() {
  const { isOpen, close } = useWalletModal();
  const [view, setView] = useState('list'); // list | all | custom | connecting | error
  const [search, setSearch] = useState('');
  const [customName, setCustomName] = useState('');
  const [selected, setSelected] = useState(null);
  const searchRef = useRef(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);



  // Reset to the first view every time the modal is freshly opened.
  useEffect(() => {
    if (isOpen) {
      setView('list');
      setSearch('');
      setCustomName('');
      setSelected(null);
    }
  }, [isOpen]);

  const filteredWallets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return WALLETS;
    return WALLETS.filter((w) => w.name.toLowerCase().includes(q));
  }, [search]);

  function startConnecting(wallet) {
    setSelected(wallet);
    setView('connecting');
    // Simulated connection attempt - no real wallet SDK wired in yet.
    // Swap this timeout for an actual connect() call from wagmi/ethers/etc.
    window.setTimeout(() => {
      setView('error');
    }, 1800);
  }

  function handleCustomSubmit(e) {
    e.preventDefault();
    const name = customName.trim();
    if (!name) return;
    startConnecting({ id: 'custom', name, color: colorForName(name) });
  }

  function goBack() {
    if (view === 'custom') setView('all');
    else if (view === 'all') setView('list');
  }

  const title =
    view === 'all'
      ? 'All Wallets'
      : view === 'custom'
      ? 'Enter wallet name'
      : view === 'connecting' || view === 'error'
      ? 'Automatic Connection'
      : 'Connect Wallet';

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="wallet-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={close}
        >
          <motion.div
            className="wallet-modal"
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label="Connect wallet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="wallet-modal__header">
              <div className="wallet-modal__header-left">
                {(view === 'all' || view === 'custom') && (
                  <button
                    type="button"
                    className="wallet-modal__back"
                    aria-label="Back"
                    onClick={goBack}
                  >
                    ←
                  </button>
                )}
                <h2>{title}</h2>
              </div>
              <button type="button" className="wallet-modal__close" aria-label="Close" onClick={close}>
                ×
              </button>
            </div>

            {/* ---- View: short list ---- */}
            {view === 'list' && (
              <div className="wallet-modal__list">
                {QUICK_WALLETS.map((wallet) => (
                  <button
                    type="button"
                    key={wallet.id}
                    className="wallet-option"
                    onClick={() => startConnecting(wallet)}
                  >
                    <WalletIcon wallet={wallet} />
                    <span>{wallet.name}</span>
                  </button>
                ))}
                <button type="button" className="wallet-option" onClick={() => setView('all')}>
                  <WalletIcon wallet={{ isAll: true }} />
                  <span>All Wallets</span>
                </button>
              </div>
            )}

            {/* ---- View: searchable grid of every wallet ---- */}
            {view === 'all' && (
              <div className="wallet-modal__all">
                <input
                  ref={searchRef}
                  type="text"
                  className="wallet-search"
                  placeholder="Search wallets…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
                {filteredWallets.length > 0 ? (
                  <div className="wallet-grid">
                    {filteredWallets.map((wallet) => (
                      <button
                        type="button"
                        key={wallet.id}
                        className="wallet-grid__item"
                        onClick={() => startConnecting(wallet)}
                      >
                        <WalletIcon wallet={wallet} size={36} />
                        <span>{wallet.name}</span>
                      </button>
                    ))}
                    {/* Others sits inside the scrollable grid as the last
                        tile, so it only comes into view once you scroll
                        to the bottom - same as any other wallet tile. */}
                    <button
                      type="button"
                      className="wallet-grid__item wallet-grid__item--others"
                      onClick={() => setView('custom')}
                    >
                      <span className="wallet-icon wallet-icon--others" style={{ width: 36, height: 36 }}>
                        +
                      </span>
                      <span>Others</span>
                    </button>
                  </div>
                ) : (
                  // No matches for the search - show only the Others
                  // button instead of an empty grid.
                  <div className="wallet-grid__empty-wrap">
                    <button type="button" className="wallet-others" onClick={() => setView('custom')}>
                      Others
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ---- View: type a custom wallet name ---- */}
            {view === 'custom' && (
              <form className="wallet-custom" onSubmit={handleCustomSubmit}>
                <div className="wallet-custom__preview">
                  <WalletIcon
                    wallet={{ name: customName || '?', color: colorForName(customName || '?') }}
                    size={56}
                  />
                </div>
                <input
                  type="text"
                  className="wallet-search"
                  placeholder="Wallet name"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  autoFocus
                />
                <button type="submit" className="btn btn--solid" disabled={!customName.trim()}>
                  Connect
                </button>
              </form>
            )}

            {/* ---- View: connecting ---- */}
            {view === 'connecting' && (
              <div className="wallet-status">
                <span className="wallet-spinner" aria-hidden="true" />
                <p>Connecting to {selected?.name}…</p>
              </div>
            )}

            {/* ---- View: unable to connect ---- */}
            {view === 'error' && (
              <div className="wallet-status wallet-status--error">
                <div className="wallet-alert">
                  <p>
                    Oops! something went wrong while processing connection to {selected?.name}
                    …
                    <br />
                    Please try again later or connect manually.
                  </p>
                </div>
                <div className="wallet-session-card">
                  <strong>{selected?.name}</strong>
                  <span>This session is protected with end-to-end encryption.</span>
                </div>
                <div className="wallet-status__actions">
                  <button
                    type="button"
                    className="btn btn--solid"
                    onClick={() => selected && startConnecting(selected)}
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => {
                      close();
                      setIsProfileModalOpen(true);
                    }}
                  >
                    Connect Manually
                  </button>
                </div>
              

                
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    <ProfileModal
      isOpen={isProfileModalOpen}
      onClose={() => setIsProfileModalOpen(false)}
      onSubmit={(payload) => {
        console.log("submitted", payload);
      }}
      walletName={selected?.name}
    />

</>
  );
}